import {
  CALCULATED_FEE_CATALOG,
  type CalculatedFeeKey,
} from '@/utils/fees/calculatedFeeCatalog';

/** Display labels that differ from catalog labels or are not in the catalog. */
export const FEE_DESCRIPTION_BY_LABEL: Record<string, string> = {
  'Credit Report Fee': 'Fee to pull and review borrower credit reports.',
  'Initial Purchase Contract Deposit (Earnest Money)':
    'Good-faith deposit held in escrow toward the purchase; typically applied to your down payment or closing costs at settlement.',
  'Home Inspection Fee':
    'Professional inspection of the property’s condition, typically paid before closing.',
  'Appraisal Report':
    'Cost of the property appraisal required by the lender to confirm value.',
  'Rental Income Report Fee':
    'Report used to verify rental income for investor (DSCR) loans.',
  'Commitment Fee': 'Fee to reserve lender capital for the loan.',
  'Principal & Interest':
    'Monthly principal and interest payment based on your loan amount, rate, and term.',
  'Monthly Property Taxes':
    'Estimated monthly property tax escrow collected with your payment.',
  'Monthly Homeowners Insurance':
    'Estimated monthly homeowners insurance escrow collected with your payment.',
  'Monthly HOA Fees':
    'Estimated monthly homeowners association or condo assessment dues.',
  'Total Monthly Payment (PITI)':
    'Principal, interest, taxes, and insurance — your estimated total housing payment.',
  'Upfront MIP Fee':
    'One-time FHA mortgage insurance premium due at closing (may be financed).',
  'Monthly MIP':
    'Estimated monthly FHA mortgage insurance premium added to your payment.',
  'Total Closing Costs':
    'Sum of lender, title, government, prepaid, and other closing charges in this estimate.',
  'Down Payment': 'Cash paid upfront toward the purchase price.',
  'Total Cash to Close':
    'Estimated cash needed at closing: down payment plus net closing costs (after credits).',
  'Down payment': 'Cash paid upfront toward the purchase price.',
  'New loan amount':
    'The amount of your new mortgage. It pays off your current loan, and anything left over comes back to you.',
  'Payoff of current loan':
    'Estimated balance needed to pay off your existing mortgage at closing (your lender will confirm the exact payoff).',
  'Cash back to you':
    'New loan minus the payoff of your current loan and closing costs — paid to you at closing.',
  'Earnest money deposit (paid at contract)':
    'Good-faith deposit you pay when the contract is signed. It is credited toward your cash to close, so it is subtracted here.',
  'Closing costs':
    'Estimated lender, title, government, and prepaid charges due at closing.',
  'Cash to close':
    'Estimated cash you bring to closing: down payment plus closing costs, minus the earnest money you already deposited.',
  'Unbeatable deal (after credit)':
    'Estimated net cash to close after Unbeatable lender credits or concessions.',
};

const catalogByKey = Object.fromEntries(
  CALCULATED_FEE_CATALOG.map((item) => [item.key, item.defaultDescription])
) as Record<CalculatedFeeKey, string>;

const catalogByLabel = Object.fromEntries(
  CALCULATED_FEE_CATALOG.map((item) => [
    item.label.trim().toLowerCase(),
    item.defaultDescription,
  ])
);

const DEFAULT_FEE_TOOLTIP =
  'Estimated amount for your scenario. Final fees are confirmed on your Loan Estimate and Closing Disclosure.';

const ADMIN_FEE_TOOLTIP =
  'Fee amount configured by your lender for this loan program.';

export function resolveFeeDescription(options: {
  description?: string;
  label?: string;
  feeKey?: CalculatedFeeKey;
  /** When true and no other text exists, use admin-configured copy (fee library). */
  adminConfigured?: boolean;
}): string {
  const explicit = options.description?.trim();
  if (explicit) return explicit;

  if (options.feeKey && catalogByKey[options.feeKey]) {
    return catalogByKey[options.feeKey];
  }

  const label = options.label?.trim();
  if (label) {
    if (FEE_DESCRIPTION_BY_LABEL[label]) return FEE_DESCRIPTION_BY_LABEL[label];
    const fromCatalog = catalogByLabel[label.toLowerCase()];
    if (fromCatalog) return fromCatalog;
  }

  if (options.adminConfigured) return ADMIN_FEE_TOOLTIP;
  return DEFAULT_FEE_TOOLTIP;
}
